import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { User, Lock, ArrowRight, Loader2 } from 'lucide-react';

export default function Login() {
    const [isLogin, setIsLogin] = useState(true);
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [successMsg, setSuccessMsg] = useState('');
    const [showSubscribeLink, setShowSubscribeLink] = useState(false);
    const [loading, setLoading] = useState(false);
    const allowSignups = true; // process.env.REACT_APP_ALLOW_NEW_SIGNUPS !== 'false';

    const { login, signup } = useAuth();

    // Check for payment success redirect
    React.useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get('mode') === 'signup' || params.get('payment') === 'success') {
            setIsLogin(false);
            if (params.get('payment') === 'success') {
                setSuccessMsg("Payment received! Please create your account to unlock your perks.");
            }
        }
    }, []);

    async function handleSubmit(e) {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            if (isLogin) {
                await login(email, password);
            } else {
                // PRE-FLIGHT CHECK: 
                // Verify email has a subscription BEFORE hitting Firebase
                const validationResp = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/v1/auth/check-subscription?email=${encodeURIComponent(email)}`);

                if (validationResp.status === 404) {
                    setShowSubscribeLink(true);
                    throw new Error("No subscription found. Please purchase a plan first.");
                }
                if (validationResp.status === 429) {
                    throw new Error("Too many attempts. Please try again later.");
                }
                if (!validationResp.ok) {
                    throw new Error("Unable to verify subscription. Please try again.");
                }

                // If check passes, proceed to Firebase signup
                await signup(email, password, name);
            }
        } catch (err) {
            // Check if it's a Firebase error or our custom error
            const msg = err.message.replace("Firebase: ", "");
            setError(msg);
        } finally {
            setLoading(false);
        }
    }

    return (
        <div className="min-h-screen flex items-center justify-center p-4 animate-enter">
            <div className="glass-panel p-10 rounded-[2.5rem] w-full max-w-md relative overflow-hidden">
                {/* Decorative background blob */}
                <div className="absolute -top-20 -right-20 w-40 h-40 bg-[var(--accent-color)]/20 blur-3xl rounded-full pointer-events-none" />

                <div className="text-center mb-10 relative z-10">
                    <h1 className="text-4xl font-black text-[var(--text-primary)] mb-3 tracking-tight">
                        {isLogin ? 'Welcome Back' : 'Join the Table'}
                    </h1>
                    <p className="text-[var(--text-secondary)] font-medium">
                        {isLogin ? 'Login to access your personalized meal plans.' :
                            allowSignups ? 'Get started with AI-powered nutrition.' :
                                'Sign-ups are temporarily paused. Existing users can still log in.'}
                    </p>
                </div>

                {successMsg && (
                    <div className="bg-emerald-500/10 text-emerald-600 p-4 rounded-xl mb-6 text-sm font-bold border border-emerald-500/20 flex items-center gap-2 animate-pulse">
                        <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                        {successMsg}
                    </div>
                )}

                {error && (
                    <div className="bg-red-500/10 text-red-500 p-4 rounded-xl mb-6 text-sm font-bold border border-red-500/20 flex flex-col gap-3">
                        <div className="flex items-center gap-2">
                            <div className="w-1.5 h-1.5 rounded-full bg-red-500" />
                            {error}
                        </div>
                        {showSubscribeLink && (
                            <a
                                href="https://cathys-sous-chef.com/#pricing"
                                className="bg-red-500 text-white py-2 px-4 rounded-lg text-center hover:bg-red-600 transition-colors"
                            >
                                Get a Subscription
                            </a>
                        )}
                    </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-5 relative z-10">
                    {!isLogin && (
                        <div className="relative group">
                            <User className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--text-secondary)] w-5 h-5 group-focus-within:text-[var(--accent-color)] transition-colors" />
                            <input
                                type="text"
                                placeholder="Full Name"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                className="input-premium w-full pl-12 bg-[var(--card-surface)]/50"
                                required={!isLogin}
                            />
                        </div>
                    )}

                    <div className="relative group">
                        <User className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--text-secondary)] w-5 h-5 group-focus-within:text-[var(--accent-color)] transition-colors" />
                        <input
                            type="email"
                            placeholder="Email Address"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="input-premium w-full pl-12 bg-[var(--card-surface)]/50"
                            required
                        />
                    </div>

                    <div className="relative group">
                        <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--text-secondary)] w-5 h-5 group-focus-within:text-[var(--accent-color)] transition-colors" />
                        <input
                            type="password"
                            placeholder="Password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="input-premium w-full pl-12 bg-[var(--card-surface)]/50"
                            required
                        />
                    </div>

                    <button
                        disabled={loading || (!isLogin && !allowSignups)}
                        className="w-full bg-[var(--accent-color)] hover:brightness-110 text-white p-4 rounded-2xl font-bold shadow-lg shadow-[var(--accent-color)]/20 transition-all active:scale-95 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {loading ? <Loader2 className="animate-spin w-5 h-5" /> : (
                            <>
                                {isLogin ? 'Sign In' : 'Create Account'}
                                <ArrowRight className="ml-2 w-4 h-4" />
                            </>
                        )}
                    </button>
                </form>

                <div className="mt-8 text-center relative z-10">
                    {allowSignups ? (
                        <button
                            onClick={() => setIsLogin(!isLogin)}
                            className="text-sm text-[var(--text-secondary)] hover:text-[var(--accent-color)] font-bold transition-colors"
                        >
                            {isLogin ? "Don't have an account? Sign up" : "Already have an account? Log in"}
                        </button>
                    ) : (
                        !isLogin && (
                            <button
                                onClick={() => setIsLogin(true)}
                                className="text-sm text-[var(--accent-color)] font-bold transition-colors"
                            >
                                Back to Log In
                            </button>
                        )
                    )}
                </div>
            </div>
        </div>
    );
}
